import {ScanProgressState} from "app/services/domain/waf-assessment-run.service";
import * as Models from "app/services/proxy/model/models";

import {ChartColorUtilities, ColorGradientContinuous, ColorGradientStop} from "framework/ui/charting/core/colors";
import {ControlOption} from "framework/ui/control-option";

export function WafGetRunStatusFriendly(status: Models.WellArchitectedFrameworkAssessmentRunStatus): string
{
    switch (status)
    {
        case Models.WellArchitectedFrameworkAssessmentRunStatus.Completed:
            return "Approved";

        case Models.WellArchitectedFrameworkAssessmentRunStatus.Abandoned:
            return "Abandoned";

        case Models.WellArchitectedFrameworkAssessmentRunStatus.InProgress:
            return "In Progress";
    }

    return null;
}

export function WafGetStatusFriendly(status: Models.WellArchitectedFrameworkAssessmentRunAnswerStatus): string
{
    switch (status)
    {
        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open:
            return "Open";

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified:
            return "Verified";

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException:
            return "Verified with Exception";

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable:
            return "Not Applicable";

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered:
            return "Unanswered";

        default:
            return "";
    }
}

export function WafGetRiskFriendly(risk: Models.WellArchitectedFrameworkRisk): string
{
    switch (risk)
    {
        case Models.WellArchitectedFrameworkRisk.High:
            return "High";

        case Models.WellArchitectedFrameworkRisk.Medium:
            return "Medium";

        case Models.WellArchitectedFrameworkRisk.Low:
            return "Low";

        case Models.WellArchitectedFrameworkRisk.None:
            return "None";
    }

    return "";
}

export function WafGetScanFriendly(scanState: ScanProgressState): string
{
    switch (scanState)
    {
        case ScanProgressState.Pending:
            return "Pending";

        case ScanProgressState.ErrorStarting:
        case ScanProgressState.ErrorDuring:
            return "Error";

        case ScanProgressState.InProgress:
            return "In Progress";

        case ScanProgressState.Complete:
            return "Completed";
    }
}

export function WafGetPolicyRiskFriendly(risk: Models.PolicySeverityRisk): string
{
    switch (risk)
    {
        case Models.PolicySeverityRisk.high:
            return "High";

        case Models.PolicySeverityRisk.medium:
            return "Medium";

        case Models.PolicySeverityRisk.low:
            return "Low";

        case Models.PolicySeverityRisk.critical:
            return "Critical";

        default:
            return "";
    }
}

export function WafGetContainerRiskFriendly(risk: Models.SecurityFindingsSeverity): string
{
    switch (risk)
    {
        case Models.SecurityFindingsSeverity.high:
            return "High";

        case Models.SecurityFindingsSeverity.medium:
            return "Medium";

        case Models.SecurityFindingsSeverity.low:
            return "Low";

        case Models.SecurityFindingsSeverity.critical:
            return "Critical";

        case Models.SecurityFindingsSeverity.informational:
            return "Informational";

        case Models.SecurityFindingsSeverity.none:
            return "None";

        default:
            return "";
    }
}

export function WafGetNatureFriendly(nature: Models.WellArchitectedFrameworkBestPracticeNature): string
{
    switch (nature)
    {
        case Models.WellArchitectedFrameworkBestPracticeNature.Manual:
            return "Manual";

        case Models.WellArchitectedFrameworkBestPracticeNature.Automated:
            return "Automated";
    }

    return "";
}

export function WafGetStatusColor(status: Models.WellArchitectedFrameworkAssessmentRunAnswerStatus): string
{
    switch (status)
    {
        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified:
            return ChartColorUtilities.getColorById("Severity Colors", "low").hex;

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException:
            return ChartColorUtilities.getColorById("CoreStack Green", "green500").color;

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable:
            return ChartColorUtilities.getColorById("Severity Colors", "invalid").hex;

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open:
            return ChartColorUtilities.getColorById("CoreStack", "coreStackBlue").hex;

        case Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered:
            return ChartColorUtilities.getColorById("CoreStack", "coreStackGrey").hex;
    }

    return "";
}

export function WafGetScanColor(scanState: ScanProgressState): string
{
    switch (scanState)
    {
        case ScanProgressState.Pending:
        case ScanProgressState.InProgress:
            return ChartColorUtilities.getColorById("Severity Colors", "medium").hex;

        case ScanProgressState.ErrorStarting:
        case ScanProgressState.ErrorDuring:
            return ChartColorUtilities.getColorById("Severity Colors", "high").hex;

        case ScanProgressState.Complete:
            return ChartColorUtilities.getColorById("Severity Colors", "low").hex;
    }
}

export function WafScanIncomplete(scanState: ScanProgressState): boolean
{
    switch (scanState)
    {
        case ScanProgressState.Pending:
        case ScanProgressState.ErrorStarting:
        case ScanProgressState.InProgress:
            return true;
    }

    return false;
}

export function WafGetRiskColor(risk: Models.WellArchitectedFrameworkRisk): string
{
    switch (risk)
    {
        case Models.WellArchitectedFrameworkRisk.High:
            return ChartColorUtilities.getColorById("Severity Colors", "critical").hex;

        case Models.WellArchitectedFrameworkRisk.Medium:
            return ChartColorUtilities.getColorById("Severity Colors", "high").hex;

        case Models.WellArchitectedFrameworkRisk.Low:
            return ChartColorUtilities.getColorById("Severity Colors", "medium").hex;

        case Models.WellArchitectedFrameworkRisk.None:
            return ChartColorUtilities.getColorById("Severity Colors", "low").hex;
    }

    return "";
}

export function WafGetPolicyRiskColor(risk: Models.PolicySeverityRisk): string
{
    switch (risk)
    {
        case Models.PolicySeverityRisk.critical:
            return ChartColorUtilities.getColorById("Severity Colors", "critical").hex;

        case Models.PolicySeverityRisk.high:
            return ChartColorUtilities.getColorById("Severity Colors", "high").hex;
        
        case Models.PolicySeverityRisk.info:
            return ChartColorUtilities.getColorById("Severity Colors", "info").hex;
        
        case Models.PolicySeverityRisk.low:
            return ChartColorUtilities.getColorById("Severity Colors", "low").hex;

        case Models.PolicySeverityRisk.medium:
            return ChartColorUtilities.getColorById("Severity Colors", "medium").hex;

        case Models.PolicySeverityRisk.low:
            return ChartColorUtilities.getColorById("Severity Colors", "low").hex;

        case Models.PolicySeverityRisk.none:
            return ChartColorUtilities.getColorById("Severity Colors", "none").hex;
        
        case Models.PolicySeverityRisk.unknown:
            return ChartColorUtilities.getColorById("Severity Colors", "unknown").hex;
    }
}

export const WafAssessmentRunTerminationWarning = "Are you sure? You will be unable to continue this run afterward.";

export const WafStatusOptions = [
    new ControlOption(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open,
                      WafGetStatusFriendly(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Open)),
    new ControlOption(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable,
                      WafGetStatusFriendly(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.NotApplicable)),
    new ControlOption(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException,
                      WafGetStatusFriendly(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.VerifiedWithException)),
    new ControlOption(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified,
                      WafGetStatusFriendly(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Verified)),
    new ControlOption(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered,
                      WafGetStatusFriendly(Models.WellArchitectedFrameworkAssessmentRunAnswerStatus.Unanswered))
] as const;

export const WafRiskOptions = [
    new ControlOption(Models.WellArchitectedFrameworkRisk.High, "High"),
    new ControlOption(Models.WellArchitectedFrameworkRisk.Medium, "Medium"),
    new ControlOption(Models.WellArchitectedFrameworkRisk.Low, "Low"),
    new ControlOption(Models.WellArchitectedFrameworkRisk.None, "None")
] as const;

export const WafProgressColorStops = [
    new ColorGradientStop(0, ChartColorUtilities.getColorById("Severity Colors", "critical").hex),
    new ColorGradientStop(25, ChartColorUtilities.getColorById("Severity Colors", "high").hex),
    new ColorGradientStop(60, ChartColorUtilities.getColorById("Severity Colors", "medium").hex),
    new ColorGradientStop(100, ChartColorUtilities.getColorById("Severity Colors", "low").hex)
] as const;

export const WafProgressColorMapper = new ColorGradientContinuous(WafProgressColorStops.map((stop) => new ColorGradientStop(stop.value, stop.color)));
